/**
 * Call Status Indicator Component
 *
 * @module modules/queue/presentation/components/CallStatusIndicator
 *
 * Displays call status with animated pulsing dot and text.
 * Used in queue cards to show real-time call state.
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { memo } from 'react';

import type { CallStatus } from '../../domain/types';

// ============================================================================
// Types
// ============================================================================

interface CallStatusIndicatorProps {
  /** The current call status */
  status: CallStatus;
  /** Whether to show the status text (default: true) */
  showText?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for call status
 */
function getStatusLabel(status: CallStatus): string {
  switch (status) {
    case 'in_queue':
      return 'In Queue';
    case 'dialing':
      return 'Dialing...';
    case 'ringing':
      return 'Ringing...';
    case 'active':
      return 'On Call';
    case 'ended':
    case 'closed':
      return 'Ended';
    case 'forwarding':
      return 'Forwarding...';
    case 'cancelled':
      return 'Cancelled';
    case 'failed':
      return 'Failed';
    case 'forwarded':
      return 'Forwarded';
    case 'pending':
      return 'Pending';
    case 'followUp':
      return 'Follow Up';
    default:
      return status;
  }
}

/**
 * Get color for call status
 */
function getStatusColor(status: CallStatus): string {
  switch (status) {
    case 'in_queue':
    case 'pending':
      return 'text.secondary';
    case 'dialing':
      return 'info.main';
    case 'ringing':
      return 'warning.main';
    case 'active':
      return 'success.main';
    case 'forwarding':
      return 'info.main';
    case 'ended':
    case 'closed':
      return 'text.disabled';
    case 'cancelled':
    case 'failed':
      return 'error.main';
    case 'forwarded':
      return 'text.disabled';
    case 'followUp':
      return 'secondary.main';
    default:
      return 'text.secondary';
  }
}

/**
 * Check if status should have pulsing animation
 */
function shouldPulse(status: CallStatus): boolean {
  return status === 'dialing' || status === 'ringing' || status === 'active' || status === 'forwarding';
}

// ============================================================================
// Component
// ============================================================================

/**
 * Call Status Indicator Component
 *
 * Displays call status with animated pulsing dot and text.
 *
 * Status display:
 * - in_queue: Gray dot, "In Queue" text
 * - dialing: Blue pulsing dot, "Dialing..." text
 * - ringing: Orange pulsing dot, "Ringing..." text
 * - active: Green pulsing dot, "On Call" text
 * - ended/closed: Gray dot, "Ended" text
 * - cancelled/failed: Red dot, "Failed" or "Cancelled" text
 *
 * @example
 * ```tsx
 * <CallStatusIndicator status="dialing" />
 * <CallStatusIndicator status="active" showText={false} />
 * ```
 */
export const CallStatusIndicator = memo(function CallStatusIndicator({ status, showText = true, size = 'small' }: CallStatusIndicatorProps) {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);
  const isPulsing = shouldPulse(status);

  const dotSize = size === 'small' ? 8 : 10;
  const fontSize = size === 'small' ? '0.75rem' : '0.875rem';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {/* Status Dot */}
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
          ...(isPulsing && {
            animation: 'statusPulse 1.5s ease-in-out infinite',
            '@keyframes statusPulse': {
              '0%, 100%': {
                opacity: 1,
                transform: 'scale(1)',
              },
              '50%': {
                opacity: 0.5,
                transform: 'scale(1.3)',
              },
            },
          }),
        }}
      />

      {/* Status Text */}
      {showText && (
        <Typography
          variant="caption"
          sx={{
            fontSize,
            fontWeight: 500,
            color,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
});
